<template>
  <hb-basic-page
    ref="surveysPage"
    :action-buttons="$vuetify.breakpoint.smAndDown ? { add: titleButton } : {}"
    :back-button="{ show: true, fallback: 'tenant_start' }"
    :fill-height="true"
    :loading="loading"
    :search-title="`${$t('surveys.search')}...`"
    :subtle-loading="subtleLoading"
    :title="$tc('surveys.title', 2)"
    :title-button="!readonly ? titleButton : null"
    name="surveys-view"
    @handle-search="handleSearch">

    <v-slide-x-transition>
      <div v-if="!loading && filteredRichSurveys.length > 0 && imageIsLoaded" class="bg"></div>
    </v-slide-x-transition>
    <img src="img/undraw_analyze_re_9kco.svg" style="display: none;" @load="imageIsLoaded = true"/>

    <v-col v-if="!loading && richSurveys.length > 0" class="pb-10" cols="12" lg="8" md="10" offset-lg="2"
           offset-md="1"
           offset-xl="3" xl="6">
      <v-row>
        <v-col cols="12">
          <section-header icon="mdi mdi-clipboard-list-outline">{{ $t('surveys.latest_surveys') }}</section-header>
          <v-list color="transparent">
            <template v-for="survey in latestSurveys">
              <v-list-item :key="survey.key"
                           :to="{name: 'tenant_single_survey', params: { id: survey.key } }"
                           class="white my-2 rounded">
                <v-list-item-content>
                  <v-list-item-title v-if="survey.surveyTemplate"
                                     class="text-wrap">{{
                      $t('surveys.type.' + survey.surveyTemplate.type) + getSurveyName(survey)
                    }}
                    <v-chip :color="getStatusColor(survey.status)" class="white--text" x-small>{{
                        $t('surveys.status.' + survey.status)
                      }}
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="survey.status < 200" class="caption">
                    {{ $t('surveys.times_by_status.' + survey.status) }}
                    {{ $formatDateNoTime(survey.status === 0 ? survey.start : survey.end) }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle v-else-if="survey.status > 100">
                    {{ $formatMonthAndYear(survey.end) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
          <v-btn v-if="!readonly" block color="primary" large outlined @click="handleCreateSurvey">
            <v-icon left>add</v-icon>
            {{ $t('surveys.create_survey') }}
          </v-btn>
        </v-col>
      </v-row>
      <v-row v-if="pastSurveys.length > 0">
        <v-col cols="12">
          <section-header icon="mdi mdi-history">{{ $t('surveys.survey_history') }}</section-header>
          <v-list color="transparent">
            <template v-for="survey in pastSurveys">
              <v-list-item :key="survey.key" :to="{name: 'tenant_single_survey', params: { id: survey.key } }"
                           class="white my-2 rounded">
                <v-list-item-content>
                  <v-list-item-title v-if="survey.surveyTemplate" class="text-wrap">{{
                      $t('surveys.type.' + survey.surveyTemplate.type) + getSurveyName(survey)
                    }}
                  </v-list-item-title>
                  <v-list-item-subtitle>{{ $formatMonthAndYear(survey.end) }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-col>
      </v-row>
    </v-col>

    <v-col v-if="!loading && filteredRichSurveys.length === 0" class="pa-0 fill-height" cols="12">
      <v-row align="center" class="fill-height" no-gutters>
        <no-content-view v-if="!loading && richSurveys.length === 0"
                         :description="$t('surveys.create_first_survey_help')"
                         :description-extra="$t('surveys.after_survey_is_created_help')"
                         :function-button="!$isReadOnlyUser"
                         :function-button-text="$t('surveys.create_survey')"
                         :prop-function="handleCreateSurvey"
                         :reload-button="false"
                         :title="$t('surveys.create_first_survey')"
                         function-button-icon="add"
                         title-image="img/undraw_customer_survey_re_v9cj.svg"/>
        <no-content-view v-else-if="!loading && richSurveys.length && filteredRichSurveys.length === 0"
                         :description="$t('surveys.no_surveys_found_help')"
                         :function-button="true"
                         :function-button-text="$t('surveys.clear_search')"
                         :prop-function="clearSearch"
                         :reload-button="false"
                         :title="$t('surveys.no_surveys_found')"
                         function-button-icon="clear"
                         title-image="img/undraw_customer_survey_re_v9cj.svg"/>
      </v-row>
    </v-col>

  </hb-basic-page>
</template>

<script>
import NoContentView from "@/components/NoContentView";
import SectionHeader from "@/components/SectionHeader";
import surveyApi from "@/api/survey";
import dayjs from "dayjs";

const statusColors = {
  0: 'warning',
  100: 'blue',
  200: 'success',
}

export default {
  name: 'SurveysView',
  components: {SectionHeader, NoContentView},
  data() {
    return {
      loading: true,
      subtleLoading: false,
      searchPhrase: '',
      titleButton: {
        text: this.$t('surveys.create_survey'),
        icon: 'add',
        callback: this.handleCreateSurvey,
        primary: true,
      },
      surveys: [],
      imageIsLoaded: false,
    };
  },
  computed: {
    richSurveys() {
      const now = dayjs();
      let surveys = this.surveys.map(s => {
        if (dayjs(s.start).isAfter(now)) {
          s.status = 0;
        } else if (dayjs(s.end).isAfter(now)) {
          s.status = 100;
        } else {
          s.status = 200;
        }
        return s;
      });
      surveys.sort((a, b) => {
        return new Date(b.end) - new Date(a.end);
      });
      surveys = surveys.map(s => {
        s.searchable = [
          s?.name?.toLowerCase(),
          s?.surveyTemplate?.name?.toLowerCase(),
          s?.surveyTemplate?.type?.toLowerCase(),
          this.$t('surveys.type.' + s?.surveyTemplate?.type).toLowerCase(),
          this.$formatDateNoTime(s?.start).toLowerCase(),
          dayjs(s?.start).format('MMMM').toLowerCase(),
          this.$formatDateNoTime(s?.end).toLowerCase(),
          dayjs(s?.end).format('MMMM').toLowerCase(),
        ].filter(Boolean).join(' ');
        return s;
      });
      return surveys;
    },
    filteredRichSurveys() {
      return this.richSurveys.filter(s => s.searchable.includes(this.searchPhrase.toLowerCase()));
    },
    latestSurveys() {
      let surveys = [...this.filteredRichSurveys];

      const personnelSurveys = surveys.filter(s => s.surveyTemplate.type === 'PERSONNEL' && s.status === 100);
      const managementSurveys = surveys.filter(s => s.surveyTemplate.type === 'MANAGEMENT' && s.status === 100);

      let latestSurveys = [];
      if (personnelSurveys.length === 0) {
        const firstPersonnelSurveyIndex = surveys.findIndex(s => s.surveyTemplate.type === 'PERSONNEL');
        if (firstPersonnelSurveyIndex > -1) {
          latestSurveys.push(surveys[firstPersonnelSurveyIndex]);
        }
      } else {
        latestSurveys = [...personnelSurveys];
      }
      if (managementSurveys.length === 0) {
        const firstManagementSurveyIndex = surveys.findIndex(s => s.surveyTemplate.type === 'MANAGEMENT');
        if (firstManagementSurveyIndex > -1) {
          latestSurveys.push(surveys[firstManagementSurveyIndex]);
        }
      } else {
        latestSurveys = [...latestSurveys, ...managementSurveys];
      }

      return latestSurveys;

    },
    pastSurveys() {
      let surveys = [...this.filteredRichSurveys];
      const firstPersonnelSurveyIndex = surveys.findIndex(s => s.surveyTemplate.type === 'PERSONNEL');
      if (firstPersonnelSurveyIndex > -1) {
        surveys.splice(firstPersonnelSurveyIndex, 1);
      }

      const firstManagementSurveyIndex = surveys.findIndex(s => s.surveyTemplate.type === 'MANAGEMENT');
      if (firstManagementSurveyIndex > -1) {
        surveys.splice(firstManagementSurveyIndex, 1);
      }

      return surveys;
    },
    readonly() {
      return this.$isReadOnlyUser;
    }
  },
  methods: {
    clearSearch() {
      this.$refs.surveysPage.clearSearch();
    },
    handleSearch(searchPhrase) {
      this.searchPhrase = searchPhrase;
    },
    handleCreateSurvey() {
      this.$router.push({name: 'tenant_create_survey'});
    },
    getStatusTextColor(status) {
      return statusColors[status] + '--text';
    },
    getStatusColor(status) {
      return statusColors[status];
    },
    getSurveyName(survey) {
      const name = survey.name ? ` (${survey.name}) ` : '';
      return name;
    },
    async reload() {
      if (this.surveys.length > 0) {
        this.subtleLoading = true;
        this.loading = false;
      } else {
        this.subtleLoading = false;
        this.loading = true;
      }
      this.surveys = await surveyApi.getSurveys();
      this.loading = false;
      this.subtleLoading = false;
    }
  },
  mounted() {
    if (!this.$hasMatchingTenantRoles(['tenant_admin', 'tenant_readonly'])) {
      this.$router.replace({name: 'access_denied'});
      return;
    }
    this.reload();
  },
};
</script>

<style lang="scss" scoped>
@media screen and (min-width: 960px) {
  .bg {
    position: absolute;
    background-image: url('/img/undraw_analyze_re_9kco.svg');
    background-size: contain;
    background-position: bottom;
    z-index: 0;
    bottom: 3vw;
    right: 3vw;
    width: 20vw;
    height: 20vw;
  }
}
</style>
